import "./GenericSurveyTabPage.scss";

import {
  AddIcon,
  Alert,
  Button,
  ExclamationTriangleIcon,
  Flex,
  Header,
  Text,
} from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useUserInfo } from "../../_shared/actions/useUserInfo";
import { GenericSurveyList } from "../../_shared/components/GenericSurveyListComponent";
import { TeamsLoader } from "../../_shared/components/TeamsLoader";
import { getHelpAppId, getPageContext } from "../../_shared/utils/teamsHelpers";
import { useChannelSchedules } from "../../survey/actions/useChannelSchedules";
import { ContentScope, useChannelSurveys } from "../../survey/actions/useChannelSurveys";
import { useHubspotChat } from "../actions/useHubspotChat";
import { useNps } from "../actions/useNps";
import { useTeamsContext } from "../state/TeamsContextProvider";
import { clientEventTracker } from "../utils/clientTelemetry";
import { startAuthoringTask } from "../utils/startTaskWrapper";
import { MeetingEducationBanner } from "./Banners/MeetingEducationBanner";
import { EmptyResultsComponent } from "./EmptyResultsComponent";
import { NavBarNew } from "./NavBar/NavBarNew";
import { BreadcrumbLink, NavigationHeader, NavigationItem } from "./NavigationHeader";
import { PersonalTabEmptyState } from "./PersonalTabEmptyState";
import { PollySearchNoResultsComponent } from "./PollySearchNoResultsComponent";

type GenericSurveyTabPageProps = {
  loadingMessage: string;
  showSurveys?: boolean;
  showSchedules?: boolean;
  hideCreate?: boolean;
  noResultsContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  createBtnContent?: React.ReactNode;
  breadcrumbs?: BreadcrumbLink[];
  navigationItems?: NavigationItem[];
  scope: ContentScope;
  pageName: "chat" | "team" | "personal" | "meeting";
};

export const GenericSurveyTabPage: React.FC<GenericSurveyTabPageProps> = ({
  loadingMessage,
  showSurveys,
  showSchedules,
  headerContent,
  createBtnContent,
  hideCreate,
  breadcrumbs,
  navigationItems,
  scope,
  pageName,
}) => {
  const { surveyError, loadSurveys, isSurveysLoading, surveys } = useChannelSurveys();
  const { loadSchedules, isSchedulesLoading, schedules } = useChannelSchedules();
  const { user, loadUser } = useUserInfo();
  const [query, setQuery] = useState("");

  const { teamsContext } = useTeamsContext();
  const { loadHubspotChat } = useHubspotChat();
  const { loadNpsSurveyIfEligible } = useNps(pageName);

  const loadSurveyCallback = useCallback(() => {
    if (teamsContext && showSurveys) {
      loadSurveys(teamsContext, scope, { includeResponses: true, excludeTypes: ["trivia"] });
    }
  }, [teamsContext, showSurveys, scope, loadSurveys]);

  useEffect(() => loadSurveyCallback(), [loadSurveyCallback]);

  const loadSchedulesCallback = useCallback(() => {
    if (teamsContext && showSchedules) {
      loadSchedules(teamsContext, scope, { includeResponses: true });
    }
  }, [teamsContext, showSchedules, scope, loadSchedules]);

  useEffect(loadSchedulesCallback, [loadSchedulesCallback]);

  useEffect(() => {
    (async () => {
      if (user.subscriptions.length > 0) {
        await loadHubspotChat(teamsContext);
      }
      clientEventTracker.track("tab page viewed", user, {
        url: window.location.pathname,
        pageName,
        locale: teamsContext.app.locale,
        platform: teamsContext.app.osLocaleInfo?.platform,
        hostClientType: teamsContext.app.host.clientType,
        tenantSKU: teamsContext.user?.tenant?.teamsSku,
        theme: teamsContext.app.theme,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadHubspotChat, teamsContext, pageName]);

  useEffect(() => {
    loadNpsSurveyIfEligible();
  }, [loadNpsSurveyIfEligible]);

  const filteredItems = useMemo(() => {
    const filteredSurveys = surveys.filter(survey =>
      survey.title.toLowerCase().includes(query.toLowerCase())
    );
    const filteredSchedules = schedules.filter(schedule =>
      (schedule.template?.title || schedule.name).toLowerCase().includes(query.toLowerCase())
    );
    return [...filteredSurveys, ...filteredSchedules];
  }, [query, schedules, surveys]);

  const showPersonalTabEmptyState = useMemo(() => {
    return (
      !surveyError &&
      surveys.length + schedules.length === 0 &&
      getPageContext() === "/msteams" &&
      user.featureFlags.Test_2021Q2PersonalTab !== "blank" &&
      pageName === "personal"
    );
  }, [
    pageName,
    schedules.length,
    surveyError,
    surveys.length,
    user.featureFlags.Test_2021Q2PersonalTab,
  ]);

  const showChatOrTeamCTAState = useMemo(() => {
    return (
      getPageContext() === "/msteams" &&
      ((user.featureFlags.Test_2021Q2TeamTab === "cta" && pageName === "team") ||
        (user.featureFlags.Test_2021Q2ChatTab === "cta" && pageName === "chat"))
    );
  }, [pageName, user.featureFlags.Test_2021Q2ChatTab, user.featureFlags.Test_2021Q2TeamTab]);

  const notInstalled = !!surveyError?.body?.notInstalled;

  const showLoading =
    (!!showSurveys && isSurveysLoading) || (!!showSchedules && isSchedulesLoading);

  const onAuthoringCompletion = () => {
    loadSurveyCallback();
    loadSchedulesCallback();
  };

  const authoringCallback = useCallback(() => {
    if (loadUser) {
      loadUser();
    }
    loadSurveyCallback();
    loadSchedulesCallback();
  }, [loadSchedulesCallback, loadSurveyCallback, loadUser]);
  return (
    <Flex column gap="gap.medium" className="generic-survey-tab-page">
      <NavBarNew
        onSearch={
          showLoading
            ? undefined
            : async value => {
                setQuery(value);
              }
        }
      />

      {getPageContext() === "/msteams" && <MeetingEducationBanner />}

      <NavigationHeader
        breadcrumbs={breadcrumbs}
        navigationItems={notInstalled ? undefined : navigationItems}
        actions={
          hideCreate || notInstalled
            ? undefined
            : [
                {
                  key: "add",
                  icon: <AddIcon />,
                  primary: true,
                  disabled: showLoading,
                  content: createBtnContent ?? "Create",
                  onClick: async () => {
                    if (pageName !== "personal") {
                      startAuthoringTask(authoringCallback);
                    } else {
                      startAuthoringTask(authoringCallback, true);
                    }
                  },
                },
              ]
        }
      />

      {headerContent}

      <TeamsLoader isLoading={showLoading} loadingMessage={loadingMessage}>
        {notInstalled && (
          <Flex column hAlign="center" gap="gap.medium">
            <Header as="h2" color="brand">
              Welcome!
            </Header>
            <Text
              size="large"
              content={`It looks like ${
                getPageContext() === "/agile" ? "AgilePolly" : "Polly"
              } is not installed as an application yet to this team!`}
            />

            <Text
              size="large"
              content={`Once Polly is installed to ${teamsContext.team?.displayName} you can begin sending and creating Pollys!`}
            />

            <Button
              content={getPageContext() === "/agile" ? "Install AgilePolly!" : "Install Polly!"}
              primary
              onClick={() => {
                const appId = getHelpAppId();
                app.openLink(`https://teams.microsoft.com/l/app/${appId}?source=tab-page`);
              }}
            />
          </Flex>
        )}

        {showPersonalTabEmptyState && <PersonalTabEmptyState />}

        {surveyError && !notInstalled && (
          <Alert
            danger
            content={surveyError.message || "Error loading page"}
            icon={<ExclamationTriangleIcon />}
          />
        )}

        {!surveyError &&
          (filteredItems.length > 0 ? (
            <GenericSurveyList
              genericSurveys={filteredItems}
              loadSeriesCallback={loadSchedulesCallback}
              loadInstanceCallback={loadSurveyCallback}
            />
          ) : query ? (
            <PollySearchNoResultsComponent />
          ) : (
            showChatOrTeamCTAState && (
              <EmptyResultsComponent onAuthoringCompletion={onAuthoringCompletion} />
            )
          ))}
      </TeamsLoader>
    </Flex>
  );
};
